import { useState, useRef, useCallback, useEffect } from 'react'
import { Loader2, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/utils/cn'
import { getInitials, getAvatarColor } from '@/utils/avatarUtils'
import { formatUserDisplayName } from '@/utils/formatters'
import { useUserSearch } from '@/hooks/useUserSearch'
import {
  useShareDepartments,
  useDepartmentSharesWithUsers,
} from '@/hooks/useHierarchyShares'
import { toast } from '@/stores/useToastStore'
import type { UserRead } from '@/api/shares'

interface ShareDepartmentsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  departmentIds: string[]
  departmentNames: string[]
  onSuccess: () => void
}

export function ShareDepartmentsModal({
  open,
  onOpenChange,
  departmentIds,
  departmentNames,
  onSuccess,
}: ShareDepartmentsModalProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUsers, setSelectedUsers] = useState<UserRead[]>([])
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const {
    data: searchData,
    isLoading: isSearching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useUserSearch({ query: searchQuery })

  const shareMutation = useShareDepartments()
  const { data: departmentSharesData, isLoading: isLoadingShares } =
    useDepartmentSharesWithUsers(departmentIds, departmentNames)

  const users = searchData?.pages.flatMap(page => page.users) ?? []

  // Filter out already selected users
  const selectedIds = new Set(selectedUsers.map(u => u.id))
  const filteredUsers = users.filter(u => !selectedIds.has(u.id))

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!listRef.current || !hasNextPage || isFetchingNextPage) return

    const { scrollTop, scrollHeight, clientHeight } = listRef.current
    if (scrollHeight - scrollTop - clientHeight < 100) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    setShowDropdown(true)
  }

  const handleInputFocus = () => {
    if (searchQuery.length >= 2) {
      setShowDropdown(true)
    }
  }

  const handleAddUser = (user: UserRead) => {
    setSelectedUsers(prev => [...prev, user])
    setSearchQuery('')
    setShowDropdown(false)
    inputRef.current?.focus()
  }

  const handleRemoveUser = (userId: string) => {
    setSelectedUsers(prev => prev.filter(u => u.id !== userId))
  }

  const handleConfirm = () => {
    if (selectedUsers.length === 0 || departmentIds.length === 0) return

    shareMutation.mutate(
      {
        departmentIds,
        userIds: selectedUsers.map(u => String(u.id)),
      },
      {
        onSuccess: result => {
          if (result.failed.length === 0) {
            toast.success(
              `Shared ${result.succeeded} department${result.succeeded !== 1 ? 's' : ''} with ${selectedUsers.length} user${selectedUsers.length !== 1 ? 's' : ''}`
            )
          } else {
            toast.warning(
              `Shared ${result.succeeded} department${result.succeeded !== 1 ? 's' : ''}, but ${result.failed.length} failed`
            )
          }
          onSuccess()
          handleClose()
        },
        onError: (error: Error) => {
          toast.error(`Failed to share: ${error.message}`)
        },
      }
    )
  }

  const handleClose = () => {
    setSearchQuery('')
    setSelectedUsers([])
    setShowDropdown(false)
    onOpenChange(false)
  }

  const shouldShowDropdown = showDropdown && searchQuery.length >= 2

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Share Departments</DialogTitle>
          <DialogDescription>
            Share {departmentIds.length} department
            {departmentIds.length !== 1 ? 's' : ''} with selected users.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Departments being shared */}
          <div className="space-y-1.5">
            <h4 className="text-sm font-medium text-foreground">Departments</h4>
            <div className="flex flex-wrap gap-1.5">
              {departmentNames.map((name, i) => (
                <span
                  key={departmentIds[i]}
                  className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs font-medium text-foreground"
                >
                  {name}
                </span>
              ))}
            </div>
          </div>

          {/* User search */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground">Share with</h4>
            <div className="relative" ref={dropdownRef}>
              <Input
                ref={inputRef}
                placeholder="Search by email or name..."
                value={searchQuery}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                className="w-full"
              />

              {shouldShowDropdown && (
                <div
                  ref={listRef}
                  onScroll={handleScroll}
                  className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-md shadow-lg max-h-[200px] overflow-y-auto z-50"
                >
                  {isSearching && !isFetchingNextPage ? (
                    <div className="px-4 py-6 text-center text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                      <span className="text-sm">Searching...</span>
                    </div>
                  ) : filteredUsers.length > 0 ? (
                    <ul className="py-1">
                      {filteredUsers.map(user => {
                        const colors = getAvatarColor(user.id)
                        return (
                          <li key={user.id}>
                            <button
                              type="button"
                              onClick={() => handleAddUser(user)}
                              className={cn(
                                'w-full flex items-center gap-3 px-3 py-2',
                                'hover:bg-muted cursor-pointer',
                                'text-left transition-colors duration-150'
                              )}
                            >
                              <Avatar className="h-8 w-8">
                                <AvatarFallback
                                  className={cn(
                                    colors.bg,
                                    colors.text,
                                    'text-xs font-medium'
                                  )}
                                >
                                  {getInitials(user.display_name, user.email)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">
                                  {user.display_name || user.email}
                                </p>
                                {user.display_name && user.email && (
                                  <p className="text-xs text-muted-foreground truncate">
                                    {user.email}
                                  </p>
                                )}
                              </div>
                            </button>
                          </li>
                        )
                      })}
                      {isFetchingNextPage && (
                        <li className="px-4 py-3 text-center">
                          <Loader2 className="h-4 w-4 animate-spin mx-auto text-muted-foreground" />
                        </li>
                      )}
                    </ul>
                  ) : (
                    <div className="px-4 py-6 text-center text-muted-foreground">
                      <span className="text-sm">
                        No users found for &quot;{searchQuery}&quot;
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Selected users chips */}
            {selectedUsers.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {selectedUsers.map(user => {
                  const colors = getAvatarColor(user.id)
                  return (
                    <span
                      key={user.id}
                      className="inline-flex items-center gap-1.5 rounded-full bg-muted pl-1 pr-2 py-1 text-xs font-medium"
                    >
                      <Avatar className="h-5 w-5">
                        <AvatarFallback
                          className={cn(
                            colors.bg,
                            colors.text,
                            'text-[10px] font-medium'
                          )}
                        >
                          {getInitials(user.display_name, user.email)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="truncate max-w-[120px]">
                        {user.display_name || user.email}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveUser(user.id)}
                        className="cursor-pointer hover:text-foreground text-muted-foreground"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  )
                })}
              </div>
            )}
          </div>

          <Separator />

          {/* People with access */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium">People with access</h3>
            {isLoadingShares ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : departmentSharesData.some(d => d.shares.length > 0) ? (
              <div className="max-h-[200px] overflow-y-auto space-y-4">
                {departmentSharesData.map(dept => (
                  <div key={dept.departmentId} className="space-y-2">
                    {departmentIds.length > 1 && (
                      <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        {dept.departmentName}
                      </h4>
                    )}
                    {dept.shares.length === 0 ? (
                      <p className="text-xs text-muted-foreground pl-1">
                        No one has access yet
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {dept.shares.map(({ share, user }) => {
                          const colors = getAvatarColor(user?.id ?? share.id)
                          return (
                            <div
                              key={share.id}
                              className="flex items-center justify-between"
                            >
                              <div className="flex items-center gap-2">
                                <Avatar className="h-8 w-8">
                                  <AvatarFallback
                                    className={cn(
                                      colors.bg,
                                      colors.text,
                                      'text-xs font-medium'
                                    )}
                                  >
                                    {getInitials(
                                      user?.display_name,
                                      user?.email
                                    )}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="text-sm font-medium">
                                    {formatUserDisplayName(user)}
                                  </p>
                                  {user?.email && (
                                    <p className="text-xs text-muted-foreground">
                                      {user.email}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <span className="text-xs text-muted-foreground">
                                Editor
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No one has access yet
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selectedUsers.length === 0 || shareMutation.isPending}
            className="bg-[#CF1F2E] hover:bg-[#b81a27]"
          >
            {shareMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Share'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
